import {
  GiftCouponForRedemption,
  GiftCouponRedeemStatus,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { fileBucketUrl } from '@shared/utils/fileUtils';

export const getStatusBadge = (status: GiftCouponRedeemStatus) => {
  switch (status) {
    case GiftCouponRedeemStatus.Requested:
      return { text: '신청됨', className: 'bg-yellow-100 text-yellow-800' };
    case GiftCouponRedeemStatus.Delivered:
      return { text: '발송완료', className: 'bg-green-100 text-green-800' };
    case GiftCouponRedeemStatus.Rejected:
      return { text: '발송불가', className: 'bg-orange-100 text-orange-800' };
    case GiftCouponRedeemStatus.Expired:
      return { text: '만료', className: 'bg-gray-100 text-gray-800' };
    case GiftCouponRedeemStatus.Refunded:
      return { text: '환불', className: 'bg-red-100 text-red-800' };
    case GiftCouponRedeemStatus.Canceled:
      return { text: '취소', className: 'bg-gray-100 text-gray-500' };
    case GiftCouponRedeemStatus.Disposed:
      return { text: '폐기', className: 'bg-slate-200 text-slate-600' };
    default:
      return { text: '사용가능', className: 'bg-blue-100 text-blue-800' };
  }
};

/**
 * "나중에 받기" 예약 건 — 교환신청은 안 했지만(Available) 수신번호까지 확정된 상태.
 * 수령자가 직접 예약한 건과, 관리자가 신청됨에서 되돌려 둔 건이 같은 모양이다.
 */
export const isPendingLaterReceive = (coupon: GiftCouponForRedemption): boolean =>
  coupon.redeemStatus === GiftCouponRedeemStatus.Available &&
  !!coupon.recipient?.phoneNumber;

/** 상태 배지 — 나중에 받기 예약 건은 '사용가능' 대신 따로 표기한다 */
export const getCouponStatusBadge = (coupon: GiftCouponForRedemption) =>
  isPendingLaterReceive(coupon)
    ? { text: '나중에 받기', className: 'bg-purple-100 text-purple-800' }
    : getStatusBadge(coupon.redeemStatus);

/** 신청됨 ↔ 나중에 받기 전환 가능 대상 (발송 전 건만) */
export const canToggleLaterReceive = (coupon: GiftCouponForRedemption): boolean =>
  coupon.redeemStatus === GiftCouponRedeemStatus.Requested ||
  isPendingLaterReceive(coupon);

export const formatDateTime = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  const y = String(d.getFullYear()).slice(2);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${y}.${m}.${day} ${h}:${min}`;
};

export const formatDateTimeSec = (date: Date | string | undefined) => {
  if (!date) return '';
  const d = new Date(date);
  const y = String(d.getFullYear()).slice(2);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  const sec = String(d.getSeconds()).padStart(2, '0');
  return `${y}.${m}.${day} ${h}:${min}:${sec}`;
};

export const formatDate = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  const y = String(d.getFullYear()).slice(2);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
};

export const formatPhone = (phone: string | undefined) => {
  if (!phone) return '-';
  const numbers = phone.replace(/[^0-9]/g, '');
  if (numbers.length === 11) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
  }
  return phone;
};

export const getOrdererInfo = (coupon: GiftCouponForRedemption) => {
  const order = typeof coupon.giftOrder === 'object' ? coupon.giftOrder : null;
  if (!order) return { name: '-', phone: '-', purchasedAt: undefined };
  return {
    name: order.bugo?.member?.fullName || '-',
    phone: order.bugo?.member?.phoneNumber || '-',
    purchasedAt: order.paymentDetail?.purchasedAt,
  };
};

/** 쿠폰이 속한 주문 _id — populate 여부와 무관하게 꺼낸다 */
export const getCouponOrderId = (coupon: GiftCouponForRedemption): string | undefined =>
  typeof coupon.giftOrder === 'string' ? coupon.giftOrder : coupon.giftOrder?._id;

/** 표기용 쿠폰번호 (fullCode의 -undefined 잔재 제거) */
export const getCouponDisplayCode = (coupon: GiftCouponForRedemption): string =>
  coupon.fullCode?.replace(/-undefined/g, '') || coupon.code || '';

export const getItemName = (coupon: GiftCouponForRedemption) => {
  if (typeof coupon.giftItem === 'object') {
    return coupon.giftItem?.name || coupon.giftItem?.giftItemInfo?.name || '-';
  }
  return '-';
};

/**
 * 선택상품명 축약 — 표 폭을 아끼려고 앞부분만 남긴다(전체는 클릭·툴팁으로).
 * "[바닐라 라떼 디저트] 바닐라 라떼 세트"처럼 앞에 붙는 대괄호 분류는 떼고
 * 실제 상품명에서 자른다 (안 그러면 3글자가 "[바닐"이 되어 못 알아본다).
 */
export const shortChoiceUnitName = (name: string, max = 3): string => {
  const stripped = name.replace(/^\s*\[[^\]]*\]\s*/, '').trim();
  const body = stripped || name.trim();
  return body.length > max ? `${body.slice(0, max)}…` : body;
};

/** 이 쿠폰의 판매가 — 구매 당시 동결값 우선, 없으면(레거시) 현재 상품 판매가 */
export const getCouponSalePrice = (
  coupon: GiftCouponForRedemption,
): number | undefined => {
  if (typeof coupon.salePriceRetail === 'number') return coupon.salePriceRetail;
  if (typeof coupon.giftItem === 'object') return coupon.giftItem?.priceRetail;
  return undefined;
};

/** 선택형 상품에서 고른 선택지의 정가 */
export const getChoiceUnitPrice = (
  coupon: GiftCouponForRedemption,
): number | undefined => {
  if (coupon.selectedChoiceUnit && typeof coupon.selectedChoiceUnit === 'object') {
    return coupon.selectedChoiceUnit.priceRegular;
  }
  return undefined;
};

export const getChoiceUnitName = (coupon: GiftCouponForRedemption) => {
  if (coupon.selectedChoiceUnit && typeof coupon.selectedChoiceUnit === 'object') {
    return coupon.selectedChoiceUnit.name;
  }
  return null;
};

export const getImageUrl = (coupon: GiftCouponForRedemption) => {
  if (
    coupon.selectedChoiceUnit &&
    typeof coupon.selectedChoiceUnit === 'object' &&
    coupon.selectedChoiceUnit.image
  ) {
    const img = coupon.selectedChoiceUnit.image;
    const imgUrl = typeof img === 'object' && 'url' in img ? img.url : img;
    if (imgUrl) return fileBucketUrl(imgUrl, 'w200');
  }
  if (typeof coupon.giftItem === 'object') {
    const giftItemImg = coupon.giftItem?.image;
    if (giftItemImg) {
      const imgUrl =
        typeof giftItemImg === 'object' && 'url' in giftItemImg
          ? giftItemImg.url
          : giftItemImg;
      if (imgUrl) return fileBucketUrl(imgUrl, 'w200');
    }
    const giftItemInfoImg = coupon.giftItem?.giftItemInfo?.image;
    if (giftItemInfoImg) {
      const imgUrl =
        typeof giftItemInfoImg === 'object' && 'url' in giftItemInfoImg
          ? giftItemInfoImg.url
          : giftItemInfoImg;
      if (imgUrl) return fileBucketUrl(imgUrl, 'w200');
    }
  }
  return '';
};
